import { useState } from 'react';
import { Plus, Pencil, Trash2, Save, X, AlertTriangle, Database, ToggleLeft } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import {
  useGlobalSettings,
  useUpsertGlobalSetting,
  useCreateGlobalSetting,
  useDeleteGlobalSetting,
  type GlobalSetting,
} from '@/hooks/use-admin';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { toast } from '@/stores/toast-store';

const ENVIRONMENTS = ['PROD', 'STAGE', 'DEV'] as const;
type Environment = (typeof ENVIRONMENTS)[number];

const ENV_BADGE: Record<Environment, string> = {
  PROD: 'bg-blue-100 text-blue-700',
  STAGE: 'bg-amber-100 text-amber-700',
  DEV: 'bg-green-100 text-green-700',
};

// ── Inline row editor ───────────────────────────────────────────────────────

interface EditRowProps {
  setting: GlobalSetting;
  onSave: (value: string | null, environment: string, description: string | null) => void;
  onCancel: () => void;
  saving: boolean;
}

function EditRow({ setting, onSave, onCancel, saving }: EditRowProps) {
  const [value, setValue] = useState(setting.value ?? '');
  const [environment, setEnvironment] = useState(setting.environment);
  const [description, setDescription] = useState(setting.description ?? '');

  return (
    <tr className="bg-blue-50/40 border-b border-border">
      {/* Key (read-only in edit mode) */}
      <td className="px-4 py-3 align-top">
        <span className="font-mono text-sm text-foreground">{setting.key}</span>
      </td>

      {/* Value */}
      <td className="px-4 py-3 align-top">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="(empty)"
          className="w-full rounded border border-border bg-white px-2 py-1 font-mono text-xs text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </td>

      {/* Environment */}
      <td className="px-4 py-3 align-top">
        <select
          value={environment}
          onChange={(e) => setEnvironment(e.target.value)}
          className="rounded border border-border bg-white px-2 py-1 text-xs text-foreground outline-none focus:border-primary"
        >
          {ENVIRONMENTS.map((env) => (
            <option key={env} value={env}>
              {env}
            </option>
          ))}
        </select>
      </td>

      {/* Description */}
      <td className="px-4 py-3 align-top">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="What is this setting for?"
          className="w-full resize-none rounded border border-border bg-white px-2 py-1 text-xs text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </td>

      {/* Actions */}
      <td className="px-4 py-3 align-top">
        <div className="flex gap-1.5">
          <button
            onClick={() => onSave(value.trim() || null, environment, description.trim() || null)}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded border border-primary bg-primary px-2 py-1 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? (
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Save className="h-3 w-3" />
            )}
            Save
          </button>
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
          >
            <X className="h-3 w-3" />
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Add new row ─────────────────────────────────────────────────────────────

interface AddRowProps {
  onSave: (key: string, value: string | null, environment: string, description: string | null) => void;
  onCancel: () => void;
  saving: boolean;
}

function AddRow({ onSave, onCancel, saving }: AddRowProps) {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [environment, setEnvironment] = useState<string>('PROD');
  const [description, setDescription] = useState('');

  function handleSave() {
    if (!key.trim()) {
      toast.error('Lookup name (key) is required');
      return;
    }
    onSave(
      key.trim().toLowerCase().replace(/\s+/g, '_'),
      value.trim() || null,
      environment,
      description.trim() || null,
    );
  }

  return (
    <tr className="bg-green-50/40 border-b border-border">
      {/* Key */}
      <td className="px-4 py-3 align-top">
        <input
          type="text"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="e.g. frontend_url"
          autoFocus
          className="w-full rounded border border-border bg-white px-2 py-1 font-mono text-xs text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
        <p className="mt-0.5 text-[10px] text-muted-foreground">snake_case, lowercase</p>
      </td>

      {/* Value */}
      <td className="px-4 py-3 align-top">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="(empty)"
          className="w-full rounded border border-border bg-white px-2 py-1 font-mono text-xs text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </td>

      {/* Environment */}
      <td className="px-4 py-3 align-top">
        <select
          value={environment}
          onChange={(e) => setEnvironment(e.target.value)}
          className="rounded border border-border bg-white px-2 py-1 text-xs text-foreground outline-none focus:border-primary"
        >
          {ENVIRONMENTS.map((env) => (
            <option key={env} value={env}>
              {env}
            </option>
          ))}
        </select>
      </td>

      {/* Description */}
      <td className="px-4 py-3 align-top">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="What is this setting for?"
          className="w-full resize-none rounded border border-border bg-white px-2 py-1 text-xs text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </td>

      {/* Actions */}
      <td className="px-4 py-3 align-top">
        <div className="flex gap-1.5">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded border border-green-600 bg-green-600 px-2 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? (
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Plus className="h-3 w-3" />
            )}
            Add
          </button>
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
          >
            <X className="h-3 w-3" />
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Platform feature switches ───────────────────────────────────────────────
//
// These live in the same global_settings table as everything else below, but a
// release kill switch is the wrong thing to edit as free text: typing "ture"
// into a value field silently leaves an unreleased feature off, or worse, on.
// So the keys that are really booleans get a real switch.
const FEATURE_SWITCHES: Array<{ key: string; name: string; description: string }> = [
  {
    key: 'esl_module_enabled',
    name: 'Shelf Labels (ESL)',
    description:
      'Electronic shelf labels. When off, the module is not offered to any tenant, the shelf-labels account kind is hidden at signup, and every ESL screen and API is unavailable. Turning it on only makes the module grantable — each tenant is still approved individually on their detail page.',
  },
  {
    key: 'companion_module_enabled',
    name: 'Campaign API',
    description:
      "Lets a store's own integration send one SMS or email at a time through the API, from the location's campaign senders, served by the separate campaign-api service. When on, each location on a business's detail page shows a Campaign API credentials panel where a super admin issues test and live credentials. When off, the panel is hidden (credentials already issued keep working until revoked).",
  },
  {
    key: 'esl_price_sync_enabled',
    name: 'Publish prices to shelf labels',
    description:
      'Send every price, name and promotion change to the label service. Deliberately separate from the switch above: granting a tenant the module should not silently start a price feed, and stopping the feed during an incident should not withdraw the module from everyone. Leave off until the label service is deployed and stores are provisioned — capturing changes with nothing to deliver them to only builds a queue. Turning it off stops capture at the database, so it costs nothing while unused.',
  },
];

function FeatureSwitches({
  settings,
  onToggle,
  saving,
}: {
  settings: GlobalSetting[];
  onToggle: (key: string, on: boolean, description: string) => void;
  saving: string | null;
}) {
  const isOn = (value: string | null | undefined) => {
    const v = (value ?? '').trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'on' || v === 'yes';
  };

  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <div className="mb-1 flex items-center gap-2">
        <ToggleLeft className="h-4 w-4 text-primary" />
        <h3 className="font-medium">Platform Features</h3>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Platform-wide release switches. Off means the feature does not exist for anyone — this is separate from granting
        a module to one business, which is still done per tenant.
      </p>
      <div className="space-y-3">
        {FEATURE_SWITCHES.map((f) => {
          const row = settings.find((s) => s.key === f.key);
          const on = isOn(row?.value);
          return (
            <div key={f.key} className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium">
                  {f.name}{' '}
                  <span
                    className={`ml-1 rounded px-1.5 py-0.5 text-[10px] ${on ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'}`}
                  >
                    {on ? 'ON' : 'OFF'}
                  </span>
                </p>
                <p className="mt-1 text-xs text-muted-foreground">{f.description}</p>
                <code className="mt-1 inline-block text-[10px] text-muted-foreground">{f.key}</code>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-label={`${f.name} — turn ${on ? 'off' : 'on'}`}
                disabled={saving === f.key}
                onClick={() => onToggle(f.key, !on, f.description)}
                className={`relative mt-1 h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                  on ? 'bg-emerald-600' : 'bg-muted-foreground/30'
                }`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                    on ? 'left-[22px]' : 'left-0.5'
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

export function AdminGlobalSettingsPage() {
  const { data, isLoading } = useGlobalSettings();
  const upsert = useUpsertGlobalSetting();
  const create = useCreateGlobalSetting();
  const remove = useDeleteGlobalSetting();

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<GlobalSetting | null>(null);
  const [togglingFlag, setTogglingFlag] = useState<string | null>(null);

  const settings: GlobalSetting[] = data?.data ?? [];

  async function handleToggleFlag(key: string, on: boolean, description: string) {
    setTogglingFlag(key);
    try {
      // Upsert, not update: the row may not exist yet in an environment whose
      // seed migration has not run. A missing row already reads as OFF, so
      // writing one is the only thing needed to turn a feature on.
      await upsert.mutateAsync({ key, value: on ? 'true' : 'false', environment: 'PROD', description });
      toast.success(`${key} turned ${on ? 'on' : 'off'}`);
    } finally {
      setTogglingFlag(null);
    }
  }

  async function handleSave(key: string, value: string | null, environment: string, description: string | null) {
    await upsert.mutateAsync({ key, value, environment, description });
    toast.success(`"${key}" updated`);
    setEditingKey(null);
  }

  async function handleCreate(key: string, value: string | null, environment: string, description: string | null) {
    await create.mutateAsync({ key, value, environment, description });
    toast.success(`"${key}" added`);
    setAddingNew(false);
  }

  async function handleDelete(setting: GlobalSetting) {
    await remove.mutateAsync(setting.key);
    toast.success(`"${setting.key}" deleted`);
    setConfirmDelete(null);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Global Settings"
        description="Runtime lookup table for environment variables and configuration values shared across all services."
      />

      {/* Info banner */}
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-sm text-amber-800">
          Changes here take effect on the next API request — no server restart needed. Do <strong>not</strong> store
          secrets (passwords, private keys, JWT secrets) in this table. Use server-level{' '}
          <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">.env</code> files for secrets.
        </p>
      </div>

      {!isLoading && <FeatureSwitches settings={settings} onToggle={handleToggleFlag} saving={togglingFlag} />}

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Database className="h-4 w-4" />
          <span>
            {settings.length} entr{settings.length === 1 ? 'y' : 'ies'}
          </span>
        </div>
        {!addingNew && (
          <button
            onClick={() => {
              setAddingNew(true);
              setEditingKey(null);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Add Setting
          </button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground">Loading…</div>
      ) : (
        <div className="rounded-lg border border-border bg-white overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50">
              <tr className="border-b border-border">
                <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 w-48">
                  Lookup Name
                </th>
                <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 w-56">
                  Lookup Value
                </th>
                <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 w-32">
                  Environment
                </th>
                <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                  Description
                </th>
                <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 w-28">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Add row at top when adding new */}
              {addingNew && (
                <AddRow onSave={handleCreate} onCancel={() => setAddingNew(false)} saving={create.isPending} />
              )}

              {settings.length === 0 && !addingNew ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">
                    No settings found. Click "Add Setting" to add the first entry.
                  </td>
                </tr>
              ) : (
                settings.map((setting) =>
                  editingKey === setting.key ? (
                    <EditRow
                      key={setting.key}
                      setting={setting}
                      onSave={(value, environment, description) =>
                        handleSave(setting.key, value, environment, description)
                      }
                      onCancel={() => setEditingKey(null)}
                      saving={upsert.isPending}
                    />
                  ) : (
                    <tr
                      key={setting.key}
                      className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                    >
                      {/* Lookup Name */}
                      <td className="px-4 py-3 align-top">
                        <code className="text-sm font-mono text-foreground">{setting.key}</code>
                      </td>

                      {/* Lookup Value */}
                      <td className="px-4 py-3 align-top">
                        {setting.value ? (
                          <code className="break-all text-xs font-mono text-foreground">{setting.value}</code>
                        ) : (
                          <span className="text-xs italic text-muted-foreground">— not set —</span>
                        )}
                      </td>

                      {/* Environment */}
                      <td className="px-4 py-3 align-top">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${
                            ENV_BADGE[setting.environment as Environment] ?? 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {setting.environment}
                        </span>
                      </td>

                      {/* Description */}
                      <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                        {setting.description ?? <span className="italic">—</span>}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 align-top">
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => {
                              setEditingKey(setting.key);
                              setAddingNew(false);
                            }}
                            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                          >
                            <Pencil className="h-3 w-3" />
                            Edit
                          </button>
                          <button
                            onClick={() => setConfirmDelete(setting)}
                            className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ),
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirm dialog */}
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Setting"
        message={`Are you sure you want to delete "${confirmDelete?.key}"? This cannot be undone and may affect services that rely on this value.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (confirmDelete) handleDelete(confirmDelete);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
